import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { PromiseWithServerResult } from "@rocicorp/zero";
import { useQuery } from "@corpuscomplete/zero-data-transformer/react";
import { PlannedMealCalendar } from "@corpuscomplete/core-ui/nutrition/planned-meal/calendar";
import { PlannedMealDetail } from "@corpuscomplete/core-ui/nutrition/planned-meal/detail";
import { queries } from "@corpuscomplete/core/zero";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import { Page, PageBody, PageContent } from "@corpuscomplete/web/components/layout";
import { Badge } from "@corpuscomplete/web-ui/components/badge";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Card, CardContent } from "@corpuscomplete/web-ui/components/card";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { PlannedMealEditorSections } from "../components/planned-meal-editor-sections";
import { PlannedMealsHeader } from "../components/planned-meals-header";
import { PlannedMealsWeekStrip } from "../components/planned-meals-week-strip";
import { PlannedMealsDateRow } from "../components/planned-meals-date-row";
import { Scope } from "@corpuscomplete/core/common/scope";

export function PlannedMealsCalendarPage({
  focusedDate,
  scope,
}: {
  focusedDate: Temporal.PlainDate;
  scope: Scope.Info;
}) {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const [spaces] = useQuery(queries.space.byMembership());
  const [myGrants] = useQuery(queries.accessGrant.myGrants());
  const [filters, setFilters] = useState<PlannedMealCalendar.Filters>({
    statuses: null,
  });
  const [selectedEntryKey, setSelectedEntryKey] = useState<string | null>(null);

  const scopeId = Scope.id(scope);
  const selectedSpace = spaces.find((space) => space.id === scopeId);
  const memberUserIds = selectedSpace?.group?.members.map((member) => member.userId) ?? [userId];
  const memberLabels = new Map(
    selectedSpace?.group?.members.map((member) => [
      member.userId,
      member.profile?.name ?? member.userId,
    ]) ?? [],
  );
  const readOnly = selectedSpace
    ? !myGrants.some(
        (grant) =>
          grant.resourceType === "space" &&
          grant.resourceScope === "contents" &&
          grant.role === "editor" &&
          grant.resourceId === selectedSpace.id,
      )
    : scopeId !== userId;

  const navigateCalendar = (date: Temporal.PlainDate, nextScopeId = scopeId) => {
    const isPersonal = nextScopeId === userId;
    void navigate({
      to: isPersonal ? "/personal/planned-meals/$date" : "/spaces/$spaceId/planned-meals/$date",
      params: isPersonal ? { date } : { spaceId: nextScopeId, date },
    });
  };

  return (
    <PlannedMealCalendar.DataProvider
      scopeId={scopeId}
      focusedDate={focusedDate}
      timeZone={Temporal.Now.timeZoneId()}
    >
      <PlannedMealsCalendarContent
        filters={filters}
        focusedDate={focusedDate}
        memberUserIds={memberUserIds}
        onFiltersChange={setFilters}
        onNavigate={navigateCalendar}
        memberLabels={memberLabels}
        onScopeChange={(nextScopeId) => navigateCalendar(focusedDate, nextScopeId)}
        readOnly={readOnly}
        scope={scope}
        selectedEntryKey={selectedEntryKey}
        setSelectedEntryKey={setSelectedEntryKey}
        userId={userId}
      />
    </PlannedMealCalendar.DataProvider>
  );
}

function PlannedMealsCalendarContent({
  focusedDate,
  filters,
  memberLabels,
  memberUserIds,
  onFiltersChange,
  onNavigate,
  onScopeChange,
  readOnly,
  scope,
  selectedEntryKey,
  setSelectedEntryKey,
  userId,
}: {
  focusedDate: Temporal.PlainDate;
  filters: PlannedMealCalendar.Filters;
  memberLabels: ReadonlyMap<string, string>;
  memberUserIds: readonly string[];
  onFiltersChange: (filters: PlannedMealCalendar.Filters) => void;
  onNavigate: (date: Temporal.PlainDate) => void;
  onScopeChange: (scopeId: string) => void;
  readOnly: boolean;
  scope: Scope.Info;
  selectedEntryKey: string | null;
  setSelectedEntryKey: (key: string | null) => void;
  userId: string;
}) {
  const viewModel = PlannedMealCalendar.useCalendarViewModel(filters);
  const loading = PlannedMealCalendar.useLoadingState();
  const selectedEntry = PlannedMealCalendar.findEntryByKey(viewModel.rows, selectedEntryKey);
  const selectedDraft = PlannedMealCalendar.findUnscheduledDraftByKey(
    viewModel.unscheduledDrafts,
    selectedEntryKey,
  );
  const selectedMealId = selectedEntry?.instance.plannedMeal.id ?? selectedDraft?.id ?? null;
  const isEditing = !!selectedMealId;

  return (
    <Page>
      <PageBody>
        <PlannedMealsHeader
          filters={filters}
          focusedDate={focusedDate}
          onFiltersChange={onFiltersChange}
          onNavigate={onNavigate}
          onScopeChange={onScopeChange}
          scope={scope}
        />
        <PageContent>
          <div className="relative flex flex-col gap-3 p-4">
            {loading.isLoading && (
              <div className="pointer-events-none absolute right-4 top-4 z-10 rounded-md border bg-background/90 px-2 py-1 text-xs text-muted-foreground shadow-sm">
                Loading planned meals...
              </div>
            )}
            <PlannedMealsWeekStrip
              focusedDate={focusedDate}
              onSelectDate={onNavigate}
              days={viewModel.weekStrip}
            />
            <div className={isEditing ? "grid gap-4 lg:grid-cols-[24rem_minmax(0,1fr)]" : "block"}>
              <div className="flex flex-col gap-3">
                {viewModel.unscheduledDrafts.length > 0 ? (
                  <Card className="gap-2 p-3" size="sm">
                    <Text className="font-medium">Unscheduled drafts</Text>
                    <div className="grid gap-2">
                      {viewModel.unscheduledDrafts.map((meal) => (
                        <button
                          key={meal.id}
                          type="button"
                          className="rounded-lg border p-2 text-left hover:bg-muted"
                          onClick={() =>
                            setSelectedEntryKey(PlannedMealCalendar.unscheduledDraftKey(meal))
                          }
                        >
                          <Text
                            className={
                              meal.name.trim() ? undefined : "italic text-muted-foreground"
                            }
                          >
                            {meal.name.trim() || "Unnamed planned meal"}
                          </Text>
                          <Text variant="xs" className="text-muted-foreground">
                            {meal.typeLabel} · no events
                          </Text>
                        </button>
                      ))}
                    </div>
                  </Card>
                ) : null}
                {viewModel.rows.map((row) => (
                  <PlannedMealsDateRow
                    key={row.date.toString()}
                    compact={isEditing}
                    memberUserIds={memberUserIds}
                    onSelectEntryKey={setSelectedEntryKey}
                    selectedEntryKey={selectedEntryKey}
                    readOnly={readOnly}
                    row={row}
                  />
                ))}
              </div>
              {selectedMealId ? (
                <PlannedMealDetail.Provider mealId={selectedMealId}>
                  <CalendarPlannedMealEditor
                    currentUserId={userId}
                    defaultScheduleDate={selectedEntry?.date ?? focusedDate}
                    defaultScheduleTimeZone={
                      selectedEntry?.instance.timeZone ?? Temporal.Now.timeZoneId()
                    }
                    memberLabels={memberLabels}
                    memberUserIds={memberUserIds}
                    onClose={() => setSelectedEntryKey(null)}
                    readOnly={readOnly}
                  />
                </PlannedMealDetail.Provider>
              ) : null}
            </div>
          </div>
        </PageContent>
      </PageBody>
    </Page>
  );
}

function CalendarPlannedMealEditor({
  currentUserId,
  defaultScheduleDate,
  defaultScheduleTimeZone,
  memberLabels,
  memberUserIds,
  onClose,
  readOnly,
}: {
  currentUserId: string;
  defaultScheduleDate: Temporal.PlainDate;
  defaultScheduleTimeZone: string;
  memberLabels: ReadonlyMap<string, string>;
  memberUserIds: readonly string[];
  onClose: () => void;
  readOnly: boolean;
}) {
  const meal = PlannedMealDetail.useMeal();
  const mutations = PlannedMealDetail.useMutations();
  const violations = PlannedMealDetail.usePublishViolations();
  const [error, setError] = useState<string | null>(null);

  if (!meal) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Text>This planned meal is no longer available.</Text>
          <Button className="mt-3" variant="outline" size="sm" onClick={onClose}>
            Back to calendar
          </Button>
        </CardContent>
      </Card>
    );
  }

  const canEditContent = !readOnly && PlannedMealDetail.canEditContent(meal);
  const canEditSchedule = !readOnly && PlannedMealDetail.canEditSchedule(meal);
  const linkedAssignments = PlannedMealDetail.hasLinkedFoodLogAssignments(meal);
  const mutate = (result: PromiseWithServerResult | undefined) => {
    if (!result) return;
    setError(null);
    watchMutation(result, setError);
  };

  const deleteDraft = () => {
    const label = meal.name.trim() || "unnamed planned meal";
    if (!window.confirm(`Delete draft ${label} and all of its scheduled events?`)) return;
    const result = mutations.deleteDraft();
    watchMutation(result, setError);
    void result.client
      .then((clientResult) => {
        if (clientResult.type === "success") onClose();
      })
      .catch(() => undefined);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="sticky top-3 z-10 rounded-xl border bg-background/95 p-3 shadow-sm backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Text className="truncate font-medium">
                {meal.name.trim() || "Unnamed planned meal"}
              </Text>
              <Badge variant={meal.status === "published" ? "default" : "outline"}>
                {meal.status}
              </Badge>
            </div>
            <Text variant="xs" className="text-muted-foreground">
              Editing in calendar
            </Text>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
            <Button
              size="sm"
              disabled={readOnly || meal.status !== "draft" || violations.length > 0}
              title={violations[0]?.message}
              onClick={() => mutate(mutations.publish())}
            >
              Publish
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={readOnly || meal.status !== "published" || linkedAssignments}
              title={
                linkedAssignments ? "Cannot unpublish while linked food logs exist" : undefined
              }
              onClick={() => {
                if (window.confirm("Unpublish this planned meal?")) mutate(mutations.unpublish());
              }}
            >
              Unpublish
            </Button>
            {meal.status === "draft" ? (
              <Button
                variant="destructive"
                size="sm"
                disabled={readOnly}
                title={readOnly ? "You do not have write access to this scope" : undefined}
                onClick={deleteDraft}
              >
                Delete draft
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      {error && <Text className="text-destructive">{error}</Text>}
      <PlannedMealEditorSections
        canEditContent={canEditContent}
        canEditSchedule={canEditSchedule}
        currentUserId={currentUserId}
        defaultScheduleDate={defaultScheduleDate}
        defaultScheduleTimeZone={defaultScheduleTimeZone}
        linkedAssignments={linkedAssignments}
        meal={meal}
        memberLabels={memberLabels}
        memberUserIds={memberUserIds}
        mutate={mutate}
        onRename={(name) => mutate(mutations.rename(name))}
        violations={violations}
      />
    </div>
  );
}

function watchMutation(
  result: PromiseWithServerResult,
  setError: (message: string | null) => void,
): void {
  void result.client
    .then((clientResult) => {
      if (clientResult.type === "error") setError(clientResult.error.message);
    })
    .catch((caught: unknown) =>
      setError(caught instanceof Error ? caught.message : "Planned meal update failed"),
    );
}
